import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { promptsAPI } from '../services/api';
import { useDarkMode } from '../context/DarkModeContext';
import toast from 'react-hot-toast';
import { FaRocket, FaTrophy, FaEye, FaStar, FaFire } from 'react-icons/fa';

const formatRating = (value) => (parseFloat(value) || 0).toFixed(1);

const Feed = () => {
  const { darkMode } = useDarkMode();
  const [featuredPrompts, setFeaturedPrompts] = useState([]);
  const [weeklyTop, setWeeklyTop] = useState([]);
  const [randomPrompts, setRandomPrompts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchFeedData = useCallback(async () => {
    setLoading(true);
    try {
      const [featuredResponse, weeklyResponse, allPromptsResponse] = await Promise.all([
        promptsAPI.getFeatured(3),
        promptsAPI.getWeeklyTop(5),
        promptsAPI.getAll({ sortBy: 'created_at', limit: 20 }),
      ]);

      const featured =
        featuredResponse.data.featuredPrompts || featuredResponse.data.prompts || [];
      setFeaturedPrompts(featured);

      setWeeklyTop(weeklyResponse.data.prompts || []);

      const allPrompts = allPromptsResponse.data.prompts || [];
      const shuffled = [...allPrompts].sort(() => 0.5 - Math.random());
      setRandomPrompts(shuffled.slice(0, 6));
    } catch (error) {
      console.error('Fetch feed data error:', error);
      toast.error('Failed to load feed');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFeedData();
  }, [fetchFeedData]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="text-center py-12">Loading your feed...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-boop-primary to-boop-secondary text-white rounded-lg p-8 mb-8">
        <h1 className="text-4xl font-bold mb-4">Welcome to Boop!</h1>
        <p className="text-xl mb-6">
          Discover amazing AI prompts, share your own, and level up your skills
        </p>
        <div className="flex gap-4">
          <Link
            to="/prompts/create"
            className="bg-white text-boop-primary px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition"
          >
            <FaRocket className="inline mr-2" />
            Create Prompt
          </Link>
          <Link
            to="/dashboard"
            className={`bg-transparent border-2 px-6 py-3 rounded-lg font-semibold transition ${
              darkMode
                ? 'border-purple-400 text-purple-400 hover:bg-purple-400 hover:text-gray-900'
                : 'border-white hover:bg-white hover:text-boop-primary'
            }`}
          >
            Browse All
          </Link>
        </div>
      </div>

      {/* Featured Prompts */}
      {featuredPrompts.length > 0 && (
        <section className="mb-12">
          <div className="flex items-center mb-6">
            <FaStar className="text-boop-accent text-2xl mr-3" />
            <h2 className="text-3xl font-bold">Featured Prompts</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {featuredPrompts.map((prompt) => (
              <Link
                key={prompt.id}
                to={`/prompts/${prompt.id}`}
                className="bg-gradient-to-br from-boop-light to-white p-6 rounded-lg shadow-md hover:shadow-xl transition card-hover border-2 border-boop-accent"
              >
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-xl font-semibold line-clamp-2 flex-1">{prompt.title}</h3>
                  <span className="ml-2 px-2 py-1 bg-boop-accent text-white text-xs rounded-full">
                    Featured
                  </span>
                </div>
                <p className="text-gray-600 mb-4 line-clamp-3">{prompt.description}</p>
                <div className="flex items-center justify-between text-sm mb-3">
                  <span className="text-gray-500">by @{prompt.username}</span>
                  <span className="text-boop-primary font-semibold">
                    Rating {formatRating(prompt.bayesian_rating)}
                  </span>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {prompt.tags?.slice(0, 3).map((tag, i) => (
                    <span key={i} className="px-2 py-1 bg-gray-100 text-xs rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Weekly Top Prompts */}
      {weeklyTop.length > 0 && (
        <section className="mb-12">
          <div className="flex items-center mb-6">
            <FaFire className="text-red-500 text-2xl mr-3" />
            <h2 className="text-3xl font-bold">This Week's Top Prompts</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {weeklyTop.map((prompt, index) => (
              <Link
                key={prompt.id}
                to={`/prompts/${prompt.id}`}
                className="bg-white p-4 rounded-lg shadow-md hover:shadow-xl transition card-hover relative"
              >
                <div className="absolute top-2 right-2 bg-gradient-to-br from-yellow-400 to-yellow-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold">
                  #{index + 1}
                </div>
                <h3 className="text-lg font-semibold mb-2 line-clamp-2 pr-8">{prompt.title}</h3>
                <p className="text-gray-600 text-sm mb-3 line-clamp-2">{prompt.description}</p>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500">@{prompt.username}</span>
                  <span className="text-boop-primary font-semibold">
                    Rating {formatRating(prompt.bayesian_rating)}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Discover More Prompts */}
      <section className="mb-12">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <FaTrophy className="text-boop-secondary text-2xl mr-3" />
            <h2 className="text-3xl font-bold">Discover More</h2>
          </div>
          <Link to="/dashboard" className="text-boop-primary hover:text-boop-secondary font-semibold">
            View All +
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {randomPrompts.map((prompt) => (
            <Link
              key={prompt.id}
              to={`/prompts/${prompt.id}`}
              className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition card-hover"
            >
              <h3 className="text-xl font-semibold mb-2 line-clamp-2">{prompt.title}</h3>
              <p className="text-gray-600 mb-4 line-clamp-3">{prompt.description}</p>
              <div className="flex items-center justify-between text-sm mb-3">
                <span className="text-gray-500">by @{prompt.username}</span>
                <div className="flex items-center gap-3 text-gray-400">
                  <span className="flex items-center">
                    <FaEye className="mr-1" /> {prompt.views_count || 0}
                  </span>
                  <span className="text-boop-primary font-semibold">
                    Rating {formatRating(prompt.bayesian_rating)}
                  </span>
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                {prompt.tags?.slice(0, 3).map((tag, i) => (
                  <span key={i} className="px-2 py-1 bg-gray-100 text-xs rounded-full">
                    {tag}
                  </span>
                ))}
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Call to Action */}
      <section className="bg-gradient-to-r from-boop-primary to-boop-secondary text-white rounded-lg p-8 text-center">
        <h2 className="text-3xl font-bold mb-4">Ready to Share Your Prompts?</h2>
        <p className="text-xl mb-6">
          Join thousands of AI enthusiasts sharing and discovering the best prompts
        </p>
        <Link
          to="/prompts/create"
          className="bg-white text-boop-primary px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition inline-block"
        >
          Create Your First Prompt
        </Link>
      </section>
    </div>
  );
};

export default Feed;
